/**
 * RequestWatchersIndicator.tsx — Eye icon showing who is currently viewing
 * this request.
 *
 * - Shows a count of active viewers next to an eye icon.
 * - On hover, a tooltip lists the names of each viewer (reviewers/PMs only;
 *   requestors see count only).
 * - The count updates live via SSE without any polling on this component.
 */

import { Eye } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useRequestWatchers } from "@/hooks/useRequestWatchers";

interface Props {
  requestId: string;
  /** When true the tooltip will list watcher names; otherwise shows count only. */
  showNames?: boolean;
}

export function RequestWatchersIndicator({ requestId, showNames }: Props) {
  const { data } = useRequestWatchers(requestId);

  const count = data?.count ?? 0;
  const names = data?.names ?? [];

  const label =
    count === 0
      ? "No one else is viewing"
      : count === 1
        ? "1 person viewing"
        : `${count} people viewing`;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 text-muted-foreground select-none cursor-default">
            <Eye className="h-4 w-4" />
            <span className="text-sm tabular-nums font-medium">{count}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[220px]">
          <p className="font-medium text-xs mb-1">{label}</p>
          {showNames && names.length > 0 && (
            <ul className="space-y-0.5">
              {names.map((name) => (
                <li
                  key={name}
                  className="text-xs text-muted-foreground truncate"
                >
                  {name}
                </li>
              ))}
            </ul>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
